import {
  Button,
  Card,
  CardBody,
  Checkbox,
  Link as HeroLink,
} from '@heroui/react';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useConsent } from '../contexts/ConsentContext';

const CookieConsentBanner: React.FC = () => {
  const {
    hasSetPreferences,
    preferences,
    updatePreferences,
    acceptAll,
    rejectNonEssential,
  } = useConsent();
  const [openSettings, setOpenSettings] = useState(false);
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  // Show only on homepage and only for non-signed-in users until preferences set
  if (hasSetPreferences || isAuthenticated || location.pathname !== '/')
    return null;

  return (
    <div className='fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6'>
      <Card className='max-w-4xl mx-auto shadow-xl'>
        <CardBody className='space-y-3'>
          <div>
            <h3 className='text-base font-semibold text-gray-900'>
              We value your privacy
            </h3>
            <p className='text-sm text-gray-600 mt-1'>
              We use cookies to enhance your experience, provide essential site
              functionality, analyze traffic, and personalize content. You can
              accept all cookies or manage your preferences. See our{' '}
              <HeroLink href='/privacy' className='text-primary'>
                Privacy Policy
              </HeroLink>{' '}
              and{' '}
              <HeroLink href='/cookies' className='text-primary'>
                Cookie Policy
              </HeroLink>{' '}
              for more details.
            </p>
          </div>

          {openSettings && (
            <div className='mt-2 p-3 bg-gray-50 rounded-lg'>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                <div>
                  <Checkbox isSelected isDisabled>
                    Necessary cookies (always on)
                  </Checkbox>
                  <p className='text-xs text-gray-500 mt-1'>
                    Required for core functionality such as security, network
                    management, and accessibility.
                  </p>
                </div>
                <div>
                  <Checkbox
                    isSelected={preferences.functional}
                    onValueChange={v => updatePreferences({ functional: v })}
                  >
                    Functional cookies
                  </Checkbox>
                  <p className='text-xs text-gray-500 mt-1'>
                    Enable improved functionality and personalization, like
                    remembering preferences.
                  </p>
                </div>
                <div>
                  <Checkbox
                    isSelected={preferences.analytics}
                    onValueChange={v => updatePreferences({ analytics: v })}
                  >
                    Analytics cookies
                  </Checkbox>
                  <p className='text-xs text-gray-500 mt-1'>
                    Help us understand how our site is used so we can make
                    improvements.
                  </p>
                </div>
                <div>
                  <Checkbox
                    isSelected={preferences.marketing}
                    onValueChange={v => updatePreferences({ marketing: v })}
                  >
                    Marketing cookies
                  </Checkbox>
                  <p className='text-xs text-gray-500 mt-1'>
                    Used to deliver advertising more relevant to you and your
                    interests.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className='flex flex-col sm:flex-row gap-3 justify-end'>
            <Button variant='flat' onClick={() => setOpenSettings(s => !s)}>
              {openSettings ? 'Hide Settings' : 'Manage Settings'}
            </Button>
            <Button variant='bordered' onClick={rejectNonEssential}>
              Reject Non-Essential
            </Button>
            <Button color='primary' onClick={acceptAll}>
              Accept All
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default CookieConsentBanner;
